/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';
import { SupportApiEnum } from '../enums/api';

@Injectable()
export class SupportApiService {

    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get retrieves all support tickets.
     * @param options
     */
    public getTicketList(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(SupportApiEnum.GET_TICKETS_ALL(), moduleAuth)
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create new support tickets.
     * @param data
     * @param options
     */
    public createTicket(data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(SupportApiEnum.POST_TICKET_CREATE(), data, moduleAuth)
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get support ticket by ID.
     * @param id
     * @param options
     */
    public getTicket(id: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(SupportApiEnum.GET_TICKET(id), moduleAuth)
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update support ticket by ID.
     * @param id
     * @param data
     * @param options
     */
    public updateTicket(id: any, data: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.put(
                SupportApiEnum.PUT_TICKET_UPDATE(id),
                data,
                moduleAuth
            ).subscribe({
                next: (response: any) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete support tickets. TODO this should only be used in dev and not in production.
     * @param id
     * @param options
     */
    public deleteTicket(id: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(
                SupportApiEnum.DELETE_TICKET(id),
                moduleAuth
            ).subscribe({
                next: (response: any) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get all comments for a support ticket.
     * @param id
     * @param cursor - include - page[after]=eyJvIjoiY3JlYXRlZF9hdCxpZCIsInYiOiJaSUN1ZldRQUFBQUFhUnpuWnlCQkNBQUEifQ==&page[size]=40
     * @param options
     */
    public getTicketComments(id: any, cursor?: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(
                SupportApiEnum.GET_TICKET_COMMENTS(id, cursor),
                moduleAuth
            ).subscribe({
                next: (response: any) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Add a new comment to a support tickets.
     * @param id
     * @param data
     * @param options
     */
    public createTicketComment(id: any, data: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(
                SupportApiEnum.POST_TICKET_COMMENTS(id),
                data,
                moduleAuth
            ).subscribe({
                next: (response: any) => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
